import { Injectable } from '@angular/core';
import { User } from '@auth0/auth0-angular';
import { SessionService } from '@core/services/session/session.service';

/**
 * Marketing attribution (CS-3375).
 *
 * Paid-media parameters arrive on the landing URL and are lost the moment the
 * browser leaves the app for Auth0 Universal Login. This service is the single
 * owner of that data on the client:
 *
 *   1. capture()  — runs once at bootstrap (APP_INITIALIZER), before the router
 *                   or the Auth0 callback handling can rewrite the URL.
 *   2. storage    — first touch is written to localStorage (via SessionService,
 *                   which `flushUser()` does NOT clear) so it survives the full
 *                   page unload of the Auth0 round trip.
 *   3. forward    — `toAuthorizationParams()` turns it into `ext-*` custom
 *                   authorization parameters on the /authorize request, which is
 *                   the Auth0-supported way to carry custom context into the
 *                   authorization transaction. The post-login Action then reads
 *                   them (see auth0/actions/post-login-marketing-attribution.js).
 *
 * The original application route is a separate concern and stays in Auth0
 * `appState` (see LoginRedirectService) — UTM params are never used to restore
 * navigation, so there is no open-redirect surface here.
 */

/** Campaign parameters (the required set). */
export const UTM_PARAM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const;

/**
 * Ad-network click identifiers. Included because a Google/Meta/Microsoft ad can
 * land a user with only a click id (auto-tagging) and no utm_* pair, which would
 * otherwise report as direct traffic.
 */
export const CLICK_ID_PARAM_KEYS = ['gclid', 'fbclid', 'msclkid'] as const;

/** The complete allowlist. Nothing outside this list is ever persisted. */
export const MARKETING_PARAM_KEYS = [...UTM_PARAM_KEYS, ...CLICK_ID_PARAM_KEYS] as const;

export type MarketingParamKey = (typeof MARKETING_PARAM_KEYS)[number];
export type MarketingParams = Partial<Record<MarketingParamKey, string>>;

export interface MarketingAttribution {
  params: MarketingParams;
  /** ISO timestamp of the first touch, for auditing/debugging the funnel. */
  captured_at: string;
}

/** localStorage key. Deliberately not one of the keys `flushUser()` removes. */
export const ATTRIBUTION_STORAGE_KEY = '_marketing_first_touch';

/** Prefix Auth0 requires for custom parameters on /authorize. */
export const AUTH0_EXT_PREFIX = 'ext-';

/** Claim written by the post-login Action once attribution is persisted. */
export const ATTRIBUTION_CLAIM = 'https://culturestudio.net/marketing_attribution';

/** Values are ad-platform tokens, not free text: keep them short. */
const MAX_VALUE_LENGTH = 128;

/**
 * Conservative allowlist of characters seen in real campaign values (letters,
 * digits, separators, percent-encoding). Anything else — angle brackets, quotes,
 * control characters — is dropped rather than escaped, so nothing that reaches
 * storage, a URL or the Auth0 transaction can carry markup or a script payload.
 */
const SAFE_VALUE_PATTERN = /^[A-Za-z0-9 _.\-~+%:/|,]+$/;

@Injectable({ providedIn: 'root' })
export class AttributionService {
  constructor(private session: SessionService) {}

  /**
   * Captures first-touch attribution from a landing URL. Idempotent: once a
   * first touch exists it is never replaced, so a later campaign click cannot
   * rewrite where the user originally came from.
   *
   * @param search a query string; defaults to the current browser URL.
   */
  public capture(search: string = window.location.search): MarketingAttribution | null {
    const existing = this.getFirstTouch();
    if (existing) {
      return existing;
    }

    const params = this.parse(search);
    if (Object.keys(params).length === 0) {
      // No campaign params: store nothing. Users who arrive organically keep
      // exactly the behaviour they had before this feature.
      return null;
    }

    const attribution: MarketingAttribution = {
      params,
      captured_at: new Date().toISOString(),
    };
    this.session.setObject(ATTRIBUTION_STORAGE_KEY, attribution);
    return attribution;
  }

  /**
   * Reads the stored first touch. localStorage is user-writable, so the payload
   * is re-validated on every read and never trusted as-is.
   */
  public getFirstTouch(): MarketingAttribution | null {
    if (!this.session.has(ATTRIBUTION_STORAGE_KEY)) {
      return null;
    }

    const stored = this.session.getObject(ATTRIBUTION_STORAGE_KEY) as MarketingAttribution;
    const params = this.sanitizeParams(stored?.params);

    if (Object.keys(params).length === 0) {
      this.session.removeObject(ATTRIBUTION_STORAGE_KEY);
      return null;
    }

    return {
      params,
      captured_at: typeof stored?.captured_at === 'string' ? stored.captured_at : '',
    };
  }

  /**
   * The stored first touch as Auth0 custom authorization parameters, e.g.
   * `{ 'ext-utm_source': 'facebook' }`. Empty when there is nothing to send, so
   * the /authorize request for a non-campaign user is byte-for-byte unchanged.
   */
  public toAuthorizationParams(): Record<string, string> {
    const attribution = this.getFirstTouch();
    if (!attribution) {
      return {};
    }

    return Object.entries(attribution.params).reduce<Record<string, string>>((acc, [key, value]) => {
      acc[`${AUTH0_EXT_PREFIX}${key}`] = value;
      return acc;
    }, {});
  }

  /**
   * True when the post-login Action has already persisted attribution for this
   * user (the claim mirrors `app_metadata.marketing_attribution`).
   */
  public isPersistedForUser(user: User | null | undefined): boolean {
    if (!user) {
      return false;
    }
    const claim = user[ATTRIBUTION_CLAIM] ?? user['marketing_attribution'];
    return !!claim && typeof claim === 'object';
  }

  /**
   * Dev-only diagnostic: a silent no-op here means the tenant-side Action is
   * missing or not receiving the `ext-*` params, which is otherwise invisible
   * from the browser.
   */
  public warnIfNotPersisted(user: User | null | undefined): void {
    if (!this.getFirstTouch() || this.isPersistedForUser(user)) {
      return;
    }
    console.warn(
      '[attribution] first-touch marketing params were sent as ext-* authorization ' +
        'parameters but no attribution claim came back in the ID token. Verify ' +
        'app_metadata.marketing_attribution in the Auth0 dashboard before treating ' +
        'this as a failure: a claim set before the first-login form suspends the ' +
        'flow may not reach the final token, while app_metadata is still correct. ' +
        'A missing app_metadata value means the "Marketing attribution" Action is ' +
        'not deployed, not first in the Login flow, or is not receiving the params.'
    );
  }

  public clearFirstTouch(): void {
    this.session.removeObject(ATTRIBUTION_STORAGE_KEY);
  }

  /** Extracts and validates the allowlisted params from a query string. */
  private parse(search: string): MarketingParams {
    if (!search) {
      return {};
    }

    const query = new URLSearchParams(search);
    const raw: Record<string, unknown> = {};

    query.forEach((value, key) => {
      // Campaign links are frequently built with mixed casing (UTM_Source).
      raw[key.trim().toLowerCase()] = value;
    });

    return this.sanitizeParams(raw);
  }

  /** Keeps only allowlisted keys whose values pass validation. */
  private sanitizeParams(raw: unknown): MarketingParams {
    if (!raw || typeof raw !== 'object') {
      return {};
    }

    return MARKETING_PARAM_KEYS.reduce<MarketingParams>((acc, key) => {
      const value = this.sanitizeValue((raw as Record<string, unknown>)[key]);
      if (value) {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  private sanitizeValue(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const trimmed = value.trim();
    if (!trimmed || trimmed.length > MAX_VALUE_LENGTH) {
      return null;
    }

    return SAFE_VALUE_PATTERN.test(trimmed) ? trimmed : null;
  }
}
